import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ConfigService } from '@ph-core/services/config/config.service';
import { InsuranceResponseModel } from '@ph-model/api/response/insurance.response.model';
import { Forms } from '@ph-model/form/form';
import { CartItem, CartItemErrorTypes } from '@ph-shopping/model/cart/cart-item.model';
import { CartTax } from '@ph-shopping/model/cart/cart-tax.model';
import { Cart } from '@ph-shopping/model/cart/cart.model';
import { InsRequest } from '@ph-shopping/model/cart/request-quote';
import { SaveQuote } from '@ph-shopping/model/cart/save-quote.model';
import { ValidatedCart } from '@ph-shopping/model/cart/validate-cart.model';

@Injectable({ providedIn: 'root' })
export class CartApiClient {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private environment = this.configService.environment;

  validateCart(request: Cart): Observable<ValidatedCart> {
    const powerUpProducts = request.products.filter((product: CartItem) => product.product.productCode === 'GMWC');
    if (
      (powerUpProducts.length > 0 && request.products.length === powerUpProducts.length) ||
      !this.environment.features.shopping.supportCartValidation
    ) {
      return of({
        validatedCart: request.products,
        containsError: false,
        containsWarning: false,
      });
    }

    return this.http.post<ValidatedCart>(`${this.environment.apiUrl}/cartvalidation`, request).pipe(
      map((data: ValidatedCart) => ({
        ...data,
        validatedCart: data.validatedCart.map((item: CartItem) => ({
          ...item,
          errorType:
            item.errorType && typeof item.errorType === 'string'
              ? [item.errorType as unknown as CartItemErrorTypes]
              : [],
          message: item.message && typeof item.message === 'string' ? [item.message as unknown as string] : [],
        })),
      })),
      catchError(this._errorHandler)
    );
  }

  saveQuote(quote: SaveQuote) {
    return this.http.post<void>(`${this.environment.apiUrl}/saveQuote`, quote).pipe(catchError(this._errorHandler));
  }

  getRequestQuote(request: InsRequest) {
    return this.http
      .post<{ insResponse: InsuranceResponseModel }>(`${this.environment.apiUrl}/umu-insurance/quote`, request)
      .pipe(catchError(this._errorHandler));
  }

  getUMUCredit(request: InsRequest, locale?: string) {
    let headers = new HttpHeaders();
    if (locale) {
      headers = headers.set('Accept-Language', locale);
    }

    return this.http
      .post<{
        insResponse: { responseStatus: string; applicationResponse: string };
      }>(`${this.environment.apiUrl}/umu-insurance/contract`, request, { headers })
      .pipe(catchError(this._errorHandler));
  }

  getCheckoutForm(request: Cart): Observable<Forms> {
    return this.http
      .post<Forms>(`${this.environment.apiUrl}/getcheckoutform`, request)
      .pipe(catchError(this._errorHandler));
  }

  taxCalculation(request: CartTax): Observable<{ cart: CartItem[] }> {
    return this.http
      .post<{ cart: CartItem[] }>(`${this.environment.apiUrl}/taxcalculation`, request)
      .pipe(catchError(this._errorHandler));
  }

  private _errorHandler(error: HttpErrorResponse) {
    return throwError(() => error);
  }
}
